"use client";
import {
  Checkbox,
  NumericInput,
  RadioButton,
  type RadioItem,
  Typography,
  useMediaQuery,
} from "@kyan/ui";

import {
  getPerpConstraints,
  getPerpSizeDisplayPrecision,
} from "#/config/orders";
import { tooltips } from "#/config/tooltips";
import { useValidatePerpSize } from "#/hooks/useValidatePerpSize";
import { useMarket } from "#/store/app/portfolio/market";
import { usePerpIsPostOnly } from "#/store/perps/tradePanel/perpIsPostOnly";
import { usePerpLimitOrderType } from "#/store/perps/tradePanel/perpLimitOrderType";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { usePerpUnit } from "#/store/perps/tradePanel/perpUnit";
import { LimitOrder } from "#/types/orders";

import { FocusableSizeInput } from "../../../../../FocusableSizeInput/FocusableSizeInput";

const limitSubOrderTypes: RadioItem<LimitOrder>[] = [
  {
    id: LimitOrder.GTC,
    label: "GTC",
    className: "w-[54px]",
    tooltip: tooltips.GTC,
  },
  {
    id: LimitOrder.GTD,
    label: "GTD",
    className: "w-[54px]",
    tooltip: tooltips.GTD,
  },
];

export const PerpLimitOrder: React.FC = () => {
  const { isSm } = useMediaQuery();
  const { market } = useMarket();
  const { perpSizeInputValue, setPerpSizeInputValue } = usePerpSize();
  const { perpUnit, perpUnits, isUnderlyingSelected, setPerpUnit } =
    usePerpUnit();
  const { perpLimitPrice, setPerpLimitPrice } = usePerpLimitPrice();
  const { perpLimitOrderType, setPerpLimitOrderType } = usePerpLimitOrderType();
  const { perpIsPostOnly, setPerpIsPostOnly } = usePerpIsPostOnly();

  const { sizeError, sizeWarning } = useValidatePerpSize({ isLimit: true });
  const sizeDisplayPrecision = getPerpSizeDisplayPrecision(
    market.underlying,
    isUnderlyingSelected,
  );
  const { priceDisplayPrecisionNotional } = getPerpConstraints(
    market.underlying,
  );

  const handleUpdateLimitPrice = (price: string) => {
    setPerpLimitPrice(price);
  };

  const handleUpdatePerpSize = (size: string | number | undefined) => {
    setPerpSizeInputValue(String(size));
  };

  const handleToggleSubOrderType = (id: LimitOrder) => {
    setPerpLimitOrderType(id);
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <FocusableSizeInput
        selectProps={{
          defaultValue: perpUnit,
          value: perpUnit,
          items: perpUnits,
          itemClassName: "h-8 sm:h-4",
          onValueChange: setPerpUnit,
        }}
        inputProps={{
          label: isUnderlyingSelected ? "Size" : "$ Size",
          value: perpSizeInputValue,
          error: sizeError || sizeWarning,
          /* warning: sizeWarning, */
          placeholder: isUnderlyingSelected ? "0.00" : "$0",
          rightLabel: "Equity: 500 USDC",
          autoNumericOptions: {
            currencySymbol: isUnderlyingSelected ? "" : "$",
            decimalPlaces: sizeDisplayPrecision,
          },
          onChange: handleUpdatePerpSize,
        }}
      />
      <NumericInput
        rightIcon="USD"
        className="ml-auto"
        label="Limit price"
        placeholder="$0.00"
        variant="secondary"
        value={perpLimitPrice}
        rightLabel="Equity: 500 USDC"
        wrapperClassName="h-11 sm:h-8"
        autoNumericOptions={{
          currencySymbol: "$",
          decimalPlaces: priceDisplayPrecisionNotional,
        }}
        onChange={handleUpdateLimitPrice}
      />
      <div className="border-divider mt-2.5 flex items-center justify-between border-b pb-2">
        <Typography grotesk primary size={12}>
          Limit order type
        </Typography>
        <div className="flex items-center gap-2">
          <Checkbox
            label="Post only"
            value={perpIsPostOnly}
            onValueChange={setPerpIsPostOnly}
          />
          <RadioButton
            variant="tertiary"
            size={isSm ? "sm" : "md"}
            items={limitSubOrderTypes}
            value={perpLimitOrderType}
            onChange={handleToggleSubOrderType}
          />
        </div>
      </div>
    </div>
  );
};
